/**
 * Demand Trends — time-series charts of cluster + signal creation plus the
 * current denomination distribution.
 *
 * Consumes GET /api/admin/dashboard/analytics/church-demand/trends via
 * churchDemandAPI.trends({ weeks }). The weeks parameter is clamped 1..26
 * server-side with a default of 12.
 *
 * Weekly series are pre-sorted ASC by week_start (chart-ready).
 * Denomination mix includes a `null` bucket that we label "Unknown" — do NOT
 * filter it out; null is a legitimate bucket.
 */

import { useEffect, useMemo, useState } from 'react';
import { churchDemandAPI } from '../../../services/api';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const WEEKS_OPTIONS = [4, 8, 12, 26];
const CHART_HEIGHT_PX = 260;

function formatWeekLabel(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
}

function prepareWeeklySeries(rows = []) {
  return rows.map((r) => ({
    week: formatWeekLabel(r.week_start),
    count: r.count || 0,
  }));
}

function prepareDenominationMix(rows = []) {
  return rows.map((r) => ({
    denomination: r.denomination == null ? 'Unknown' : r.denomination,
    count: r.count || 0,
  }));
}

export default function DemandTrends() {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await churchDemandAPI.trends({ weeks });
        if (cancelled) return;
        setData(res.data);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Failed to load trends'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [weeks]);

  const clustersByWeek = useMemo(
    () => prepareWeeklySeries(data?.new_clusters_by_week),
    [data]
  );
  const signalsByWeek = useMemo(
    () => prepareWeeklySeries(data?.new_signals_by_week),
    [data]
  );
  const denomMix = useMemo(
    () => prepareDenominationMix(data?.denomination_mix),
    [data]
  );

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Window
        </span>
        <select
          value={weeks}
          onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-h-[36px]"
        >
          {WEEKS_OPTIONS.map((w) => (
            <option key={w} value={w}>
              Last {w} weeks
            </option>
          ))}
        </select>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
          <strong className="font-semibold">Failed to load:</strong> {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          Loading trends…
        </div>
      )}

      {!loading && !error && data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* New clusters per week */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              New clusters per week
            </h3>
            {clustersByWeek.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
                <LineChart data={clustersByWeek}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* New signals per week */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              New signals per week
            </h3>
            {signalsByWeek.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
                <LineChart data={signalsByWeek}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Denomination mix */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Denomination mix
            </h3>
            {denomMix.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
                <BarChart data={denomMix} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="denomination"
                    tick={{ fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
